class CartUpsellRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.closest('cart-drawer')?.classList.contains('active')) {
      this.loadRecommendations();
    }
  }

  async loadRecommendations() {
    const productIds = this.getProductIds();
    const list = this.querySelector('[data-cart-upsell-list]');
    const requestKey = productIds.join(',');

    if (!list || !productIds.length) {
      this.hidden = true;
      return;
    }

    if (this.dataset.loadedFor === requestKey) return;
    this.dataset.loadedFor = requestKey;
    this.hidden = true;
    list.replaceChildren();

    const cartProductIds = new Set(productIds);
    const shownProductIds = new Set(cartProductIds);
    const products = [];
    const limit = this.getPositiveInteger(this.dataset.limit, 3);

    for (const productId of productIds) {
      if (products.length >= limit || !this.isConnected) break;

      const candidates = await this.fetchRecommendations(productId);

      for (const candidate of candidates) {
        const recommendedProductId = candidate.dataset.productId;

        if (!recommendedProductId || shownProductIds.has(recommendedProductId)) continue;

        shownProductIds.add(recommendedProductId);
        products.push(candidate);

        if (products.length >= limit) break;
      }
    }

    if (products.length < limit) {
      products.push(...this.getFallbackRecommendations(shownProductIds, limit - products.length));
    }

    if (!this.isConnected) return;

    if (products.length) {
      list.replaceChildren(...products);
      this.hidden = false;
      this.classList.add('is-loaded');
    } else {
      this.hidden = true;
      this.classList.remove('is-loaded');
    }
  }

  getProductIds() {
    return [...new Set((this.dataset.productIds || '').split(',').map((id) => id.trim()).filter(Boolean))];
  }

  getPositiveInteger(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  async fetchRecommendations(productId) {
    const url = new URL(
      this.dataset.url || `${window.Shopify.routes.root}recommendations/products`,
      window.location.origin
    );
    url.searchParams.set('section_id', this.dataset.sectionId || 'cart-drawer-upsell');
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', this.getPositiveInteger(this.dataset.requestLimit, 10));
    url.searchParams.set('intent', this.dataset.intent || 'related');

    try {
      const response = await fetch(url.toString());

      if (!response.ok) return [];

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      return Array.from(html.querySelectorAll('[data-cart-upsell-product]'));
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  getFallbackRecommendations(shownProductIds, limit) {
    const fallback = this.querySelector('[data-cart-upsell-fallback]');
    const products = [];

    if (!fallback || limit <= 0) return products;

    for (const candidate of fallback.querySelectorAll('[data-cart-upsell-product]')) {
      const recommendedProductId = candidate.dataset.productId;

      if (!recommendedProductId || shownProductIds.has(recommendedProductId)) continue;

      shownProductIds.add(recommendedProductId);
      products.push(candidate);

      if (products.length >= limit) break;
    }

    return products;
  }
}

if (!customElements.get('cart-upsell-recommendations')) {
  customElements.define('cart-upsell-recommendations', CartUpsellRecommendations);
}
